import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  ModalBody,
  ModalFooter,
  Label,
  Input,
  Spinner,
  Collapse,
} from "reactstrap";
import Select from "react-select";
import { userSearchApiCall } from "../../../../Utils/searchApiCall";
import * as url from "../../../../helpers/url_helper";
import { toast } from "react-toastify";

interface UserOption {
  id: string | number;
  name: string;
  mobile: string;
}

interface DeviceItem {
  id: string;
  name?: string;
  slave?: string;
  modelType?: string;
  blasterId?: string;
  isOnline?: boolean
}

interface RoomDevices {
  id: string;
  name: string;
  chip: DeviceItem[];
  blaster: DeviceItem[];
  curtain: DeviceItem[];
  lock: DeviceItem[];
  driver: DeviceItem[];
}

interface EditAccessData {
  _id: string;
  accessId: string;
  relation?: string;
  accessType?: 0 | 1;
  expiredDate?: string | null;
  recipientUserId?: string | number;
  firstname?: string;
  lastname?: string;
  mobile?: string;
  chipIds?: string[];
  blasterRemoteIds?: string[];
  airCurtainIds?: string[];
  lockIds?: string[];
  airDriverIds?: string[];
}

interface SharedUsersModalProps {
  isOpen: boolean;
  toggle: () => void;
  onSubmit: (data: {
    relation: string;
    selectedUser: UserOption;
    accessType: "full" | "temporary";
    selectedDevices: any[];
  }) => void;
  grantorUserId: string;
  premiseId: string;
  mode?: "create" | "edit";
  editAccessData?: EditAccessData | null;
}

const RELATIONS = [
  "Father",
  "Mother",
  "Wife",
  "Husband",
  "Son",
  "Daughter",
  "Friend",
  "Other",
];

const DEVICE_TYPE_MAP = {
  chip: { field: "chipIds", label: "Chip" },
  blaster: { field: "blasterRemoteIds", label: "Blaster" },
  curtain: { field: "airCurtainIds", label: "Curtain" },
  lock: { field: "lockIds", label: "Lock" },
  driver: { field: "airDriverIds", label: "Driver" },
} as const;

type DeviceTypeKey = keyof typeof DEVICE_TYPE_MAP;
const DEVICE_TYPE_KEYS = Object.keys(DEVICE_TYPE_MAP) as DeviceTypeKey[];

function toggleInSet(set: Set<string>, id: string) {
  const next = new Set(set);
  next.has(id) ? next.delete(id) : next.add(id);
  return next;
}

function deviceLabel(type: DeviceTypeKey, device: DeviceItem) {
  if (type === "chip" || type === "curtain") {
    return `${device.slave || device.name || device.id} ${
      device.modelType ? `(${device.modelType})` : ""
    }`.trim();
  }
  return device.name || device.slave || device.id;
}

function getRoomDeviceIds(room: RoomDevices, type: DeviceTypeKey): string[] {
  return (room[type] || []).map((d) => d.id);
}

function getRoomTotalDeviceCount(room: RoomDevices): number {
  return DEVICE_TYPE_KEYS.reduce((sum, t) => sum + (room[t]?.length || 0), 0);
}

const accessTypeToString = (val: any): "full" | "temporary" | "" =>
  val === 1 ? "temporary" : val === 0 ? "full" : "";

const BASE_URL = "https://data.whitelion.in/admin-api";
const apiVersion = 1;
const getAuthToken = () => localStorage.getItem("Authorization");

function SharedUsersModal({
  isOpen,
  toggle,
  onSubmit,
  grantorUserId,
  premiseId,
  mode = "create",
  editAccessData = null,
}: SharedUsersModalProps) {
  const [relation, setRelation] = useState<string>("");
  const [otherRelation, setOtherRelation] = useState<string>("");
  const [selectedUser, setSelectedUser] = useState<UserOption | null>(null);
  const [userData, setUserData] = useState([]);
  const [step, setStep] = useState<1 | 2>(1);
  const [accessType, setAccessType] = useState<"full" | "temporary" | "">("");
  const [expiredDate, setExpiredDate] = useState<string>("");

  const [rooms, setRooms] = useState<RoomDevices[]>([]);
  const [loadingDevices, setLoadingDevices] = useState(false);
  const [devicesError, setDevicesError] = useState<string | null>(null);
  const [expandedRoomIds, setExpandedRoomIds] = useState<Set<string>>(
    new Set(),
  );

  const [selectedIds, setSelectedIds] = useState<
    Record<DeviceTypeKey, Set<string>>
  >({
    chip: new Set(),
    blaster: new Set(),
    curtain: new Set(),
    lock: new Set(),
    driver: new Set(),
  });

  const [submitting, setSubmitting] = useState(false);

  const devicesPrefilledRef = useRef(false);

  const resetState = () => {
    setStep(1);
    setRelation("");
    setOtherRelation("");
    setSelectedUser(null);
    setAccessType("");
    setExpiredDate("");
    setRooms([]);
    setExpandedRoomIds(new Set());
    setSelectedIds({
      chip: new Set(),
      blaster: new Set(),
      curtain: new Set(),
      lock: new Set(),
      driver: new Set(),
    });
    setDevicesError(null);
    devicesPrefilledRef.current = false;
  };

  const handleClose = () => {
    resetState();
    toggle();
  };

  const handleRelationSelect = (rel: string) => {
    setRelation(rel);
    if (rel !== "Other") setOtherRelation("");
  };

  const userSearch = (e: any) => {
    userSearchApiCall(e, setUserData,3, true);
  };

  const fetchRoomDevices = useCallback(async () => {
    if (!premiseId || !grantorUserId) return;
    setLoadingDevices(true);
    setDevicesError(null);
    try {
      const res = await fetch(`${BASE_URL}/v${apiVersion}/room/roomDevices`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${getAuthToken()}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ userId: grantorUserId, premiseId }),
      });
      if (!res.ok) throw new Error(`Failed to load devices (${res.status})`);
      const json = await res.json();
      const roomList: RoomDevices[] = json?.data?.rooms || [];
      setRooms(roomList);
      //  expand rooms with a preselected device instead of any device
      if (mode !== "edit") {
        setExpandedRoomIds(
          new Set(
            roomList
              .filter((r) => getRoomTotalDeviceCount(r) > 0)
              .map((r) => r.id),
          ),
        );
      }
    } catch (err: any) {
      setDevicesError(
        err?.message || "Something went wrong while loading devices.",
      );
    } finally {
      setLoadingDevices(false);
    }
  }, [premiseId, grantorUserId, mode]);

  useEffect(() => {
    if (step === 2 && rooms.length === 0 && !loadingDevices) {
      fetchRoomDevices();
    }

  }, [step]);

  // prefill form fields from editAccessData whenever the modal opens in edit mode
  useEffect(() => {
    if (isOpen && mode === "edit" && editAccessData) {
      const rel = editAccessData.relation || "";
      const isKnownRelation = RELATIONS.includes(rel);
      setRelation(isKnownRelation ? rel : rel ? "Other" : "");
      setOtherRelation(isKnownRelation ? "" : rel);

      setSelectedUser({
        id: editAccessData.recipientUserId as string | number,
        name: [editAccessData.firstname, editAccessData.lastname]
          .filter(Boolean)
          .join(" "),
        mobile: editAccessData.mobile || "",
      });

      setAccessType(accessTypeToString(editAccessData.accessType) || "full");
      setExpiredDate(
        editAccessData.expiredDate
          ? new Date(editAccessData.expiredDate).toISOString().split("T")[0]
          : "",
      );

      setStep(1);
      devicesPrefilledRef.current = false;
    }
  }, [isOpen, mode, editAccessData]);

  // once rooms are loaded, preselect devices from editAccessData
  useEffect(() => {
    if (
      mode === "edit" &&
      editAccessData &&
      rooms.length > 0 &&
      !devicesPrefilledRef.current
    ) {
      setSelectedIds({
        chip: new Set(editAccessData.chipIds || []),
        blaster: new Set(editAccessData.blasterRemoteIds || []),
        curtain: new Set(editAccessData.airCurtainIds || []),
        lock: new Set(editAccessData.lockIds || []),
        driver: new Set(editAccessData.airDriverIds || []),
      });

      setExpandedRoomIds(
        new Set(
          rooms
            .filter((r) =>
              DEVICE_TYPE_KEYS.some((t) => {
                const field = DEVICE_TYPE_MAP[t].field;
                const preselected: string[] =
                  (editAccessData as any)[field] || [];
                return getRoomDeviceIds(r, t).some((id) =>
                  preselected.includes(id),
                );
              }),
            )
            .map((r) => r.id),
        ),
      );

      devicesPrefilledRef.current = true;
    }
  }, [mode, editAccessData, rooms]);
   
  // Preselect devices when editing
  const toggleRoomExpand = (roomId: string) => {
    setExpandedRoomIds(toggleInSet(expandedRoomIds, roomId));
  };

  const toggleDevice = (type: DeviceTypeKey, id: string) => {
    setSelectedIds((prev:any) => ({
      ...prev,
      [type]: toggleInSet(prev[type], id),
    }));
  };

  const getRoomSelectionState = (
    room: RoomDevices,
  ): "all" | "some" | "none" => {
    const allIds = DEVICE_TYPE_KEYS.flatMap((t) => getRoomDeviceIds(room, t));
    if (allIds.length === 0) return "none";
    const selectedCount = allIds.filter((id) =>
      DEVICE_TYPE_KEYS.some((t) => selectedIds[t].has(id)),
    ).length;
    if (selectedCount === 0) return "none";
    if (selectedCount === allIds.length) return "all";
    return "some";
  };

  const toggleRoomSelectAll = (room: RoomDevices) => {
    const state = getRoomSelectionState(room);
    const selectAll = state !== "all";

    setSelectedIds((prev:any) => {
      const next = { ...prev };
      DEVICE_TYPE_KEYS.forEach((type) => {
        const ids = getRoomDeviceIds(room, type);
        const set = new Set(prev[type]);
        ids.forEach((id) => (selectAll ? set.add(id) : set.delete(id)));
        next[type] = set;
      });
      return next;
    });

    if (selectAll) {
      setExpandedRoomIds((prev) => toggleInSet(prev, "__noop__"));
    }
  };

  const totalSelectedDevices = DEVICE_TYPE_KEYS.reduce(
    (sum, t) => sum + selectedIds[t].size,
    0,
  );

  const selectedRoomIds = rooms
    .filter((room) =>
      DEVICE_TYPE_KEYS.some((t) =>
        getRoomDeviceIds(room, t).some((id) => selectedIds[t].has(id)),
      ),
    )
    .map((room) => room.id);

  
  const handleSubmit = async () => {
    const recipientId = selectedUser?.id ?? (selectedUser as any)?.value;

    const finalRelation =
      relation === "Other" ? otherRelation.trim() : relation;

    if (
      !finalRelation ||
      !selectedUser ||
      !accessType ||
      totalSelectedDevices === 0
    ) {
      return;
    }

    setSubmitting(true);

    const sharedDevicePayload = {
      premiseId,
      roomIds: selectedRoomIds,
      chipIds: Array.from(selectedIds.chip),
      blasterRemoteIds: Array.from(selectedIds.blaster),
      airCurtainIds: Array.from(selectedIds.curtain),
      lockIds: Array.from(selectedIds.lock),
      airDriverIds: Array.from(selectedIds.driver),
      accessType: accessType === "full" ? 0 : 1,
      expiredDate:
        accessType === "temporary" && expiredDate
          ? new Date(expiredDate).toISOString()
          : null,
      relation: finalRelation,
    };

    try {
      if (mode === "edit") {
        const payload = {
          accessId: editAccessData?._id,
          ...sharedDevicePayload,
        };

        const res = await fetch(
          `${url.BASE_URL}${url.UPDATE_SHARE_ACCESS}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw {
            status: res.status,
            data,
          };
        }
      } else {
        const payload = {
          grantorUserId,
          recipientUserId: recipientId,
          ...sharedDevicePayload,
        };

        const res = await fetch(
          `${url.BASE_URL}${url.SHARE_ACCESS_TO_USER}`,
          {
            method: "POST",
            headers: {
              Authorization: `Bearer ${getAuthToken()}`,
              "Content-Type": "application/json",
            },
            body: JSON.stringify(payload),
          }
        );

        const data = await res.json();

        if (!res.ok) {
          throw {
            status: res.status,
            data,
          };
        }
      }

      // Success
      toast.success(
        mode === "edit"
          ? "Access updated successfully."
          : "Access shared successfully."
      );

      onSubmit({
        relation: finalRelation,
        selectedUser,
        accessType,
        selectedDevices: DEVICE_TYPE_KEYS.flatMap((t) =>
          Array.from(selectedIds[t])
        ),
      });

      resetState();
      toggle();
    } catch (err: any) {
      console.error("Share access error:", err);

      // Get API validation message
      const apiMessage =
        err?.data?.errors?.[0]?.msg ||
        err?.data?.msg ||
        `Something went wrong while ${
          mode === "edit" ? "updating" : "sharing"
        } access.`;

      toast.error(apiMessage);
    } finally {
      setSubmitting(false);
    }
  };
  const customStyles = {
    menu: (provided: any) => ({
      ...provided,
      backgroundColor: "white",
      border: "1px solid #ccc",
      boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
    }),
  };

  const goNext =
    (relation !== "" && relation !== "Other"
      ? true
      : otherRelation.trim() !== "") &&
    selectedUser !== null &&
    accessType !== "" &&
    (accessType !== "temporary" || expiredDate !== "");

  const userOptions = selectedUser
    ? [
        selectedUser,
        ...userData.filter((user: UserOption) => user.id !== selectedUser.id),
      ]
    : userData;

  return (
    <Modal isOpen={isOpen} toggle={handleClose} className=" h-75 " size="lg">
      <div className="modal-header justify-content-between">
        {/* EDIT: dynamic title */}
        <h5 className="m-0">
          {mode === "edit" ? "Edit Share Access" : "Share Device Access"}
        </h5>
        <button
          className="btn p-0 d-flex align-items-center"
          style={{ border: "none" }}
          onClick={handleClose}
          title="Close"
        >
          <box-icon name="x" size="24px"></box-icon>
        </button>
      </div>

      <ModalBody style={{ minHeight: "50vh" }}>
        {step === 1 && (
          <>
            <div className="mb-3">
              <label className="form-label fw-semibold">Relation</label>
              <div className="d-flex flex-wrap gap-2">
                {RELATIONS.map((rel) => (
                  <button
                    key={rel}
                    type="button"
                    className={`btn btn-sm ${
                      relation === rel ? "btn-dark" : "btn-outline-dark"
                    }`}
                    onClick={() => handleRelationSelect(rel)}
                  >
                    {rel}
                  </button>
                ))}
              </div>
              {relation === "Other" && (
                <input
                  type="text"
                  className="form-control mt-2"
                  placeholder="Relation Name "
                  value={otherRelation}
                  onChange={(e) => setOtherRelation(e.target.value)}
                  autoFocus
                />
              )}
            </div>

           {mode === "create" && <div className="mb-4 ajax-select">
              <Label>User</Label>
              <Select
                name="user"
                value={selectedUser}
                onFocus={() => userSearch("")}
                options={userOptions}
                onChange={(selectedOption: UserOption | null) => {
                  setSelectedUser(selectedOption);
                }}
                onInputChange={(value) => {
                  userSearch(value);
                }}
                styles={customStyles}
              />
            </div>}

            <div>
              <Label className="mb-2">Access</Label>
              <div className="d-flex flex-column gap-2">
                <div className="form-check">
                  <Input
                    type="radio"
                    id="fullTimeAccess"
                    name="accessType"
                    checked={accessType === "full"}
                    onChange={() => setAccessType("full")}
                  />
                  <Label className="form-check-label" htmlFor="fullTimeAccess">
                    Full Time Access
                  </Label>
                </div>
                <div className="form-check">
                  <Input
                    type="radio"
                    id="temporaryAccess"
                    name="accessType"
                    checked={accessType === "temporary"}
                    onChange={() => setAccessType("temporary")}
                    disabled
                  />
                  <Label className="form-check-label" htmlFor="temporaryAccess">
                    Temporary Access
                  </Label>
                </div>
                {accessType === "temporary" && (
                  <Input
                    type="date"
                    className="mt-1"
                    style={{ maxWidth: 220 }}
                    value={expiredDate}
                    min={new Date().toISOString().split("T")[0]}
                    onChange={(e) => setExpiredDate(e.target.value)}
                  />
                )}
              </div>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <div className="mb-3 d-flex justify-content-between align-items-center">
              <div>
                <h6 className="fw-semibold mb-1">Select Devices</h6>
                <small className="text-muted">
                  {totalSelectedDevices} device selected
                </small>
              </div>
            </div>

            {loadingDevices && (
              <div className="d-flex align-items-center gap-2 text-muted">
                <Spinner size="sm" /> Loading devices...
              </div>
            )}


            {!loadingDevices && !devicesError && rooms.length === 0 && (
              <div className="text-muted">
                No rooms/devices found for this premise.
              </div>
            )}

            {!loadingDevices &&
              rooms.map((room) => {
                const deviceCount = getRoomTotalDeviceCount(room);
                const isExpanded = expandedRoomIds.has(room.id);
                const selState = getRoomSelectionState(room);
            //  console.log("rooms details", room)
                return (
                  <div key={room.id} className="border rounded mb-2">
                    <div
                      className="d-flex align-items-center justify-content-between p-2"
                      style={{
                        cursor: deviceCount > 0 ? "pointer" : "default",
                      }}
                      onClick={() =>
                        deviceCount > 0 && toggleRoomExpand(room.id)
                      }
                    >
                      <div className="d-flex align-items-center gap-2">
                        <Input
                          type="checkbox"
                          checked={selState === "all"}
                          ref={(el: any) => {
                            if (el) el.indeterminate = selState === "some";
                          }}
                          disabled={deviceCount === 0}
                          onClick={(e) => e.stopPropagation()}
                          onChange={() => toggleRoomSelectAll(room)}
                        />
                        <span className="fw-semibold">{room.name}</span>
                        <span className="text-muted" style={{ fontSize: 12 }}>
                          ({deviceCount} device{deviceCount !== 1 ? "s" : ""})
                        </span>
                      </div>

                      {deviceCount > 0 && (
                        <box-icon
                          name={isExpanded ? "chevron-up" : "chevron-down"}
                          size="18px"
                        ></box-icon>
                      )}
                    </div>

                    <Collapse isOpen={isExpanded}>
                      <div className="px-3 pb-3">
                        {DEVICE_TYPE_KEYS.map((type) => {
                          const items = room[type] || [];
                          if (items.length === 0) return null;
                          const { label } = DEVICE_TYPE_MAP[type];
                          return (
                            <div key={type} className="mb-2">
                              <div
                                className="text-muted fw-semibold mb-1"
                                style={{ fontSize: 12 }}
                              >
                                {label} ({items.length})
                              </div>
                              <div className="d-flex flex-wrap gap-3 ps-2">
                                {items.map((device) => {
                                  const checkboxId = `${type}-${device.id}`;
                                  
                                  return (
                                  <div
                                    key={checkboxId}
                                    className="d-flex align-items-center gap-2 px-2 py-2 rounded-3 border bg-light border border-secondary border-opacity-25 "
                                    style={{
                                      minHeight: "40px",
                                    }}
                                  >
                                    <Input
                                      type="checkbox"
                                      id={checkboxId}
                                      checked={selectedIds[type].has(device.id)}
                                      onChange={() => toggleDevice(type, device.id)}
                                      className="m-0 flex-shrink-0"
                                    />

                                    <Label
                                      htmlFor={checkboxId}
                                      className="d-flex align-items-center justify-content-between gap-2 m-0 w-100"
                                      style={{
                                        cursor: "pointer",
                                        minWidth: 0,
                                      }}
                                    >
                                      <span
                                        className="text-truncate"
                                        title={deviceLabel(type, device)}
                                        style={{
                                          fontSize: "13px",
                                          fontWeight: 500,
                                          color: "#344054",
                                          minWidth: 0,
                                        }}
                                      >
                                        {deviceLabel(type, device)}
                                      </span>

                                      <span
                                        className={`d-flex align-items-center gap-1 px-2 py-1 rounded-pill flex-shrink-0 ${
                                          device?.isOnline
                                            ? "bg-success-subtle text-success"
                                            : "bg-danger-subtle text-danger"
                                        }`}
                                        style={{
                                          fontSize: "10px",
                                          fontWeight: 600,
                                        }}
                                      >
                                        <span
                                          className={`rounded-circle ${
                                            device?.isOnline ? "bg-success" : "bg-danger"
                                          }`}
                                          style={{
                                            width: 6,
                                            height: 6,
                                          }}
                                        />

                                        {device?.isOnline ? "Online" : "Offline"}
                                      </span>
                                    </Label>
                                  </div>
                                  );
                                })}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </Collapse>
                  </div>
                );
              })}

          </>
        )}
      </ModalBody>

      <ModalFooter className="d-flex justify-content-between">
        <button
          className="btn btn-outline-secondary"
          onClick={() => (step === 2 ? setStep(1) : handleClose())}
        >
          {step === 2 ? "Back" : "Cancel"}
        </button>

        {step === 1 ? (
          <button
            className="btn btn-dark px-6 "
            disabled={!goNext}
            onClick={() => setStep(2)}
          >
            Next
          </button>
        ) : (
          <button
            className="btn btn-dark"
            disabled={totalSelectedDevices === 0 || submitting}
            onClick={handleSubmit}
          >
            {submitting ? (
              <Spinner size="sm" />
            ) : mode === "edit" ? (
              "Update"
            ) : (
              "Done"
            )}
          </button>
        )}
      </ModalFooter>
    </Modal>
  );
}

export default SharedUsersModal;